import make_query_params from '@/assets/js/functions/route/make_query_params';
import { useCacheStore } from '@/store/StoreCache.js';
import { vsprintf } from 'sprintf-js';
import pickBy from 'lodash/pickBy';
import omitBy from 'lodash/omitBy';
import { ROUTE_BASE } from '@/assets/js/consts/apiRoutes.consts';
import useAuth from '~/assets/js/composables/other/useAuth';
import { useApiClient } from '~/assets/js/network/axios_init';

const handle_options = options => {
  return Object.assign(
    {
      caching: {
        enable: false,
        name: null,
        purges: [],
      },
    },
    options
  );
};

const prepare_route_url = (route, params) => {
  const params_sprintf = pickBy(params, (_, key) => {
    return key.startsWith('%');
  });

  const params_sprintf_values = Object.values(params_sprintf);

  return vsprintf(route, params_sprintf_values);
};

const append_queries = (route, params) => {
  const filtered_params = omitBy(params, (_, key) => {
    return key.startsWith('%') || key.startsWith('__');
  });

  return route + make_query_params(filtered_params);
};

const axios_get = (route, params = {}, options = {}) => {
  options = handle_options(options);
  route = prepare_route_url(route, params);
  route = append_queries(route, params);

  const cacheStore = useCacheStore();

  cacheStore.purge_caches(options.caching.purges);

  if (options.caching.enable) {
    return cacheStore.handle_cache(options.caching.name, () =>
      useApiClient().axiosClient.get(route, {
        __silent: params.__silent,
      })
    );
  }

  return useApiClient().axiosClient.get(route, {
    __silent: params.__silent,
  });
};

const axios_patch = (route, params = {}, options = {}) => {
  options = handle_options(options);
  route = prepare_route_url(route, params);

  useCacheStore().purge_caches(options.caching.purges);

  return useApiClient().axiosClient.patch(route, params, {
    __silent: params.__silent,
  });
};

const axios_post = (route, params = {}, options = {}) => {
  options = handle_options(options);
  route = prepare_route_url(route, params);

  useCacheStore().purge_caches(options.caching.purges);

  return useApiClient().axiosClient.post(route, params, {
    ...options,
    headers: options.headers,
    __silent: params instanceof FormData ? params.get('__silent') : params.__silent,
  });
};

const axios_put = (route, params = {}, options = {}) => {
  options = handle_options(options);
  route = prepare_route_url(route, params);

  useCacheStore().purge_caches(options.caching.purges);

  return useApiClient().axiosClient.put(route, params, {
    __silent: params.__silent,
  });
};

const axios_delete = (route, params = {}, options = {}) => {
  options = handle_options(options);
  route = prepare_route_url(route, params);
  route = append_queries(route, params);

  useCacheStore().purge_caches(options.caching.purges);

  return useApiClient().axiosClient.delete(route, {
    __silent: params.__silent,
  });
};

const axios_download_file = (route, params) => {
  route = prepare_route_url(route, params);
  route = append_queries(route, params);

  const token = useAuth().getAuthToken();

  return fetch(ROUTE_BASE + route, {
    method: 'GET',
    headers: {
      ...(token && { Authorization: 'Bearer ' + token }),
    },
  })
    .then(response => {
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const disposition = response.headers.get('content-disposition');
      let filename = 'downloaded-file.xlsx';
      if (disposition && disposition.includes('filename=')) {
        filename = disposition.split('filename=')[1].replace(/['"]/g, '');
      }

      return response.blob().then(blob => ({ blob, filename }));
    })
    .then(({ blob, filename }) => {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    });
};

export { axios_get, axios_patch, axios_put, axios_post, axios_delete, axios_download_file };
